import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@workspace/ui/components/sheet'
import { Textarea } from '@workspace/ui/components/textarea'
import { useState } from 'react'
import { useMediaQuery } from '../../../../hooks/use-media-query'
import { type CopyKey, copy } from '../../copy/investor-copy'
import { usePassStartupMutation } from '../../hooks/use-investor-assistant'
import { type PassReason, passOfferKey } from './pass-offer'

const REASONS: { value: PassReason; key: CopyKey }[] = [
  { value: 'NOT_A_FIT', key: 'pass.reason.notAFit' },
  { value: 'TOO_EARLY', key: 'pass.reason.tooEarly' },
  { value: 'SMALL_TRACTION', key: 'pass.reason.smallTraction' },
  { value: 'NOT_DEPLOYING', key: 'pass.reason.notInvesting' },
]

const OFFER_KEY: Record<'written' | 'tooEarly' | 'traction', CopyKey> = {
  written: 'pass.offer.written',
  tooEarly: 'pass.offer.tooEarly',
  traction: 'pass.offer.traction',
}

/** The uppercase label over each part of the window, as the prototype sets them. */
const SECTION_LABEL = 'text-xs font-medium uppercase tracking-wider text-muted-foreground'

/**
 * Pass on a startup privately: a reason, and optionally what the investor would look again for. Nothing here
 * creates a follow. "Follow instead" hands over to the usual Follow choice, and nothing is saved until Save pass.
 */
export function PassWindow({
  startup,
  onClose,
  onPassed,
  onFollowInstead,
}: {
  startup: { id: string; name: string } | null
  onClose: () => void
  onPassed?: (startupId: string) => void
  onFollowInstead: (startupId: string) => void
}) {
  const desktop = useMediaQuery('(min-width: 640px)')
  const open = startup !== null
  const form = startup && (
    <PassForm
      key={startup.id}
      startup={startup}
      onPassed={onPassed}
      onFollowInstead={onFollowInstead}
      onDone={onClose}
    />
  )
  const title = startup ? copy('pass.window.title', { name: startup.name }) : ''
  if (desktop) {
    return (
      <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
        <DialogContent>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{copy('pass.privacy')}</DialogDescription>
          {form}
        </DialogContent>
      </Dialog>
    )
  }
  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="bottom">
        <SheetTitle>{title}</SheetTitle>
        <SheetDescription>{copy('pass.privacy')}</SheetDescription>
        {form}
      </SheetContent>
    </Sheet>
  )
}

function PassForm({
  startup,
  onPassed,
  onFollowInstead,
  onDone,
}: {
  startup: { id: string; name: string }
  onPassed?: (startupId: string) => void
  onFollowInstead: (startupId: string) => void
  onDone: () => void
}) {
  const [reason, setReason] = useState<PassReason>('TOO_EARLY')
  const [lookAgainWhen, setLookAgainWhen] = useState('')
  const pass = usePassStartupMutation()
  const offer = passOfferKey(reason, lookAgainWhen)

  const save = () =>
    pass.mutate(
      {
        path: { startupId: startup.id },
        body: {
          reason,
          ...(lookAgainWhen.trim() !== '' && { lookAgainWhen: lookAgainWhen.trim() }),
        },
      },
      {
        onSuccess: () => {
          onPassed?.(startup.id)
          onDone()
        },
      }
    )

  return (
    <div className="space-y-4">
      <fieldset className="space-y-2">
        <legend className={`mb-2 ${SECTION_LABEL}`}>{copy('pass.reason.legend')}</legend>
        {REASONS.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-2 text-sm text-foreground"
          >
            <input
              type="radio"
              name="pass-reason"
              className="size-4 accent-primary"
              checked={reason === option.value}
              onChange={() => setReason(option.value)}
            />
            {copy(option.key)}
          </label>
        ))}
      </fieldset>
      <div className="space-y-1.5">
        <label htmlFor="pass-look-again" className={`block ${SECTION_LABEL}`}>
          {copy('pass.lookAgain.label')}
        </label>
        <Textarea
          id="pass-look-again"
          value={lookAgainWhen}
          onChange={(event) => setLookAgainWhen(event.target.value)}
          maxLength={300}
          rows={2}
        />
      </div>
      {offer && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
          <span className="text-sm text-muted-foreground">{copy(OFFER_KEY[offer])}</span>
          <SecondaryButton type="button" size="sm" onClick={() => onFollowInstead(startup.id)}>
            {copy('pass.followInstead')}
          </SecondaryButton>
        </div>
      )}
      <div className="flex items-center justify-end gap-3">
        <SecondaryButton type="button" onClick={onDone}>
          {copy('pass.cancel')}
        </SecondaryButton>
        <CtaButton type="button" onClick={save} disabled={pass.isPending}>
          {copy('pass.save')}
        </CtaButton>
      </div>
    </div>
  )
}
